import { Fragment, useEffect, useState } from "react";
import Swal from 'sweetalert2';
import { IWebhook, IWebhookComSegredo, IWebhookEvento, IWebhookTeste } from "../../interfaces/IWebhook";
import WebhookService from "../../services/WebhookService";
import ApiKeyModalChave from "./ApiKeyModalChave";
import { formatarDataHora, podeReenviar, ROTULO_SITUACAO_EVENTO, validarUrlWebhook } from "./apiKeyUtils";

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

interface Props {
    // Só para Master; o Administrador sempre vê o próprio cliente.
    clienteId?: number;
}

const EXEMPLO_VALIDACAO = `// Node.js — valide antes de processar o evento
const [t, v1] = req.get('X-Ainumera-Signature').split(',').map(p => p.split('=')[1]);
const esperado = crypto.createHmac('sha256', SEGREDO).update(\`\${t}.\${corpoBruto}\`).digest('hex');
const valido = crypto.timingSafeEqual(Buffer.from(v1), Buffer.from(esperado))
  && Math.abs(Date.now() / 1000 - Number(t)) <= 300;`;

/**
 * DEVN3-631: aba Webhook do menu API Key. Um webhook por cliente, avisado quando as operações
 * assíncronas da API pública terminam. O segredo de assinatura só aparece no modal de exibição
 * única, ao criar e ao regerar; nunca vai para localStorage nem para a URL.
 */
export default function ApiKeyWebhook({ clienteId }: Props) {
    const { t } = useTranslation();

    const [webhook, setWebhook] = useState<IWebhook | null>(null);
    const [carregando, setCarregando] = useState<boolean>(true);
    const [url, setUrl] = useState<string>('');
    const [ativo, setAtivo] = useState<boolean>(true);
    const [tocouUrl, setTocouUrl] = useState<boolean>(false);
    const [salvando, setSalvando] = useState<boolean>(false);
    const [segredo, setSegredo] = useState<string | null>(null);
    const [testando, setTestando] = useState<boolean>(false);
    const [teste, setTeste] = useState<IWebhookTeste | null>(null);
    const [eventos, setEventos] = useState<IWebhookEvento[]>([]);
    const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
    const [mostrarAjuda, setMostrarAjuda] = useState<boolean>(false);
    // DEVN3-636: filtros da lista de entregas e reenvio manual.
    const [filtroEntregas, setFiltroEntregas] = useState<{ de: string; ate: string; situacao: string }>({ de: '', ate: '', situacao: '' });
    const [reenviando, setReenviando] = useState<string | null>(null);

    useEffect(() => {
        carregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clienteId]);

    async function carregar() {
        setCarregando(true);
        setTeste(null);
        const [atual, lista] = await Promise.all([WebhookService.obter(clienteId), WebhookService.listarEntregas(clienteId)]);
        aplicar(atual);
        setEventos(lista);
        setCarregando(false);
    }

    function aplicar(atual: IWebhook | null) {
        setWebhook(atual);
        setUrl(atual?.url ?? '');
        setAtivo(atual?.ativo ?? true);
        setTocouUrl(false);
    }

    const erroUrl = validarUrlWebhook(url);
    const alterou = !webhook || webhook.url !== url.trim() || webhook.ativo !== ativo;

    async function salvar() {
        setTocouUrl(true);
        if (erroUrl) return;

        setSalvando(true);
        const salvo = await WebhookService.salvar({ clienteId, url: url.trim(), ativo });
        setSalvando(false);
        if (!salvo) return;

        aplicar(salvo);
        if ('segredo' in salvo && salvo.segredo) {
            setSegredo((salvo as IWebhookComSegredo).segredo);
        } else {
            Swal.fire({ text: t('Webhook salvo com sucesso'), timer: 3000, icon: 'success', showConfirmButton: false });
        }
    }

    async function confirmar(titulo: string, texto: string, botao: string): Promise<boolean> {
        const resultado = await Swal.mixin({
            customClass: { confirmButton: 'btn btn-modal-excluir ms-5', cancelButton: 'btn btn-modal-cancelar' },
            buttonsStyling: false
        }).fire({
            title: t(titulo), text: t(texto), icon: 'warning', showCancelButton: true,
            cancelButtonText: t('Cancelar'), confirmButtonText: t(botao), reverseButtons: true
        });
        return resultado.isConfirmed;
    }

    async function regerar() {
        const ok = await confirmar('Regerar segredo',
            'O segredo atual deixa de valer na hora: as entregas seguintes só passam na validação do seu sistema depois que ele for atualizado com o novo segredo.',
            'Regerar');
        if (!ok) return;

        const regerado = await WebhookService.regerarSegredo(clienteId);
        if (regerado) {
            aplicar(regerado);
            setSegredo(regerado.segredo);
        }
    }

    async function remover() {
        const ok = await confirmar('Remover webhook',
            'As notificações param e as entregas pendentes são canceladas. O histórico de entregas continua disponível.',
            'Remover');
        if (ok && await WebhookService.remover(clienteId)) {
            Swal.fire({ text: t('Webhook removido'), timer: 3000, icon: 'success', showConfirmButton: false });
            await carregar();
        }
    }

    async function enviarTeste() {
        setTestando(true);
        setTeste(null);
        const resultado = await WebhookService.enviarTeste(clienteId);
        setTestando(false);
        if (resultado) {
            setTeste(resultado);
            await atualizarEntregas();
        }
    }

    async function atualizarEntregas() {
        setEventos(await WebhookService.listarEntregas(clienteId, filtroEntregas));
    }

    async function reenviar(eventoId: string) {
        setReenviando(eventoId);
        const resultado = await WebhookService.reenviar(eventoId, clienteId);
        setReenviando(null);
        if (!resultado) return;

        Swal.fire({
            text: resultado.sucesso
                ? t('Entrega reenviada com sucesso')
                : `${t('A entrega falhou de novo')}: ${resultado.statusHttp != null ? `HTTP ${resultado.statusHttp}` : t(resultado.erro ?? '')}`,
            icon: resultado.sucesso ? 'success' : 'warning', timer: 4000, showConfirmButton: false
        });
        await atualizarEntregas();
    }

    function alternar(id: string) {
        const novo = new Set(expandidos);
        if (novo.has(id)) novo.delete(id); else novo.add(id);
        setExpandidos(novo);
    }

    if (carregando) return <p>{t('Carregando...')}</p>;

    return <>
        <ApiKeyModalChave chave={segredo} onFechar={() => setSegredo(null)}
            titulo="Segredo do webhook" rotulo="Segredo" instrucao="Copie o segredo agora."
            aviso="Ele não será exibido novamente. Se for perdido, regere o segredo e atualize o seu sistema."
            confirmacao="Copiei o segredo e guardei em local seguro." />

        <p className="text-muted">
            {t('Avisamos este endereço quando uma operação da API pública termina (eventos operation.succeeded e operation.failed). Cada entrega é assinada com o segredo do webhook; sem webhook, o resultado fica disponível em GET /operation/:id.')}
        </p>

        <div className="row mb-3">
            <div className="col-md-8 form-group">
                <label htmlFor="webhook-url" className="form-label required">{t('URL')}</label>
                <input id="webhook-url" type="url" className={`form-control ${tocouUrl && erroUrl ? 'is-invalid' : ''}`}
                    placeholder="https://seu-sistema.com.br/webhooks/ainumera" value={url} maxLength={500}
                    onChange={(e) => setUrl(e.target.value)} onBlur={() => setTocouUrl(true)} />
                {tocouUrl && erroUrl && <div className="invalid-feedback">{t(erroUrl)}</div>}
                <div className="form-text">{t('Somente https. Endereços internos ou privados não são aceitos.')}</div>
            </div>
            <div className="col-md-4 d-flex align-items-center mt-md-8">
                <div className="form-check form-switch">
                    <input id="webhook-ativo" type="checkbox" className="form-check-input" checked={ativo}
                        onChange={(e) => setAtivo(e.target.checked)} />
                    <label htmlFor="webhook-ativo" className="form-check-label">{t('Ativo')}</label>
                </div>
            </div>
        </div>

        <div className="d-flex flex-wrap gap-3 mb-5">
            <button type="button" className="btn btn-sm btn-primario" disabled={salvando || !alterou} onClick={salvar}>
                {salvando ? t('Salvando...') : webhook ? t('Salvar alterações') : t('Cadastrar webhook')}
            </button>
            {webhook && <>
                <button type="button" className="btn btn-sm btn-light-primary" disabled={testando || alterou} onClick={enviarTeste}
                    title={alterou ? t('Salve as alterações antes de testar') : undefined}>
                    {testando ? t('Enviando...') : t('Enviar teste')}
                </button>
                <button type="button" className="btn btn-sm btn-light-warning" onClick={regerar}>{t('Regerar segredo')}</button>
                <button type="button" className="btn btn-sm btn-light-danger" onClick={remover}>{t('Remover')}</button>
            </>}
        </div>

        {teste &&
            <div className={`alert ${teste.sucesso ? 'alert-success' : 'alert-danger'}`} role="alert">
                <strong>{teste.sucesso ? t('Teste entregue') : t('Teste não entregue')}</strong>
                {teste.statusHttp != null && <> — HTTP {teste.statusHttp}</>}
                {' '}({teste.duracaoMs} ms)
                {teste.erro && <div>{t(teste.erro)}</div>}
                {teste.resposta && <pre className="mb-0 mt-2" style={{ whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}>{teste.resposta}</pre>}
            </div>}

        <button type="button" className="btn btn-link p-0 mb-3" onClick={() => setMostrarAjuda(!mostrarAjuda)}>
            {mostrarAjuda ? t('Ocultar') : t('Como validar a assinatura')}
        </button>
        {mostrarAjuda &&
            <div className="bg-light rounded p-4 mb-5">
                <p className="mb-2">
                    {t('Cada entrega traz os cabeçalhos X-Ainumera-Event, X-Ainumera-Delivery (id do evento, igual em todas as tentativas — use para ignorar repetidas) e X-Ainumera-Signature no formato t=<carimbo>,v1=<assinatura>.')}
                </p>
                <p className="mb-2">
                    {t('Calcule HMAC-SHA256 do texto "<t>.<corpo bruto da requisição>" com o segredo, em hexadecimal minúsculo, compare com v1 e recuse carimbos com mais de 5 minutos de diferença. Responda 2xx em até 10 segundos; qualquer outra resposta é reenviada até 8 vezes, com intervalos crescentes de 30 segundos a 1 hora.')}
                </p>
                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{EXEMPLO_VALIDACAO}</pre>
            </div>}

        <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mt-5 mb-2">
            <h4 className="mb-0">{t('Últimas entregas')}</h4>
            <div className="d-flex flex-wrap align-items-end gap-2">
                <div>
                    <label htmlFor="entregas-de" className="form-label mb-1 fs-7">{t('De')}</label>
                    <input id="entregas-de" type="date" className="form-control form-control-sm" value={filtroEntregas.de}
                        onChange={(e) => setFiltroEntregas({ ...filtroEntregas, de: e.target.value })} />
                </div>
                <div>
                    <label htmlFor="entregas-ate" className="form-label mb-1 fs-7">{t('Até')}</label>
                    <input id="entregas-ate" type="date" className="form-control form-control-sm" value={filtroEntregas.ate}
                        onChange={(e) => setFiltroEntregas({ ...filtroEntregas, ate: e.target.value })} />
                </div>
                <div>
                    <label htmlFor="entregas-situacao" className="form-label mb-1 fs-7">{t('Situação')}</label>
                    <select id="entregas-situacao" className="form-select form-select-sm" value={filtroEntregas.situacao}
                        onChange={(e) => setFiltroEntregas({ ...filtroEntregas, situacao: e.target.value })}>
                        <option value="">{t('Todas')}</option>
                        {Object.entries(ROTULO_SITUACAO_EVENTO).map(([valor, r]) => <option key={valor} value={valor}>{t(r.texto)}</option>)}
                    </select>
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={atualizarEntregas}>
                    <i className="fas fa-sync-alt"></i> {t('Atualizar')}
                </button>
            </div>
        </div>

        {eventos.length === 0 ? <p className="text-muted">{t('Nenhuma entrega ainda.')}</p>
            : <div className="table-responsive">
                <table className="table table-row-dashed align-middle gs-0 gy-3">
                    <thead>
                        <tr className="fw-bolder text-muted">
                            <th></th>
                            <th>{t('Data')}</th>
                            <th>{t('Evento')}</th>
                            <th>{t('Operação')}</th>
                            <th>{t('Situação')}</th>
                            <th>{t('Tentativas')}</th>
                            <th>{t('Última resposta')}</th>
                            <th className="text-end">{t('Ações')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {eventos.map((evento) => {
                            const rotulo = ROTULO_SITUACAO_EVENTO[evento.situacao] ?? { texto: evento.situacao, classe: 'badge-light' };
                            const ultima = evento.historico[0];
                            const aberto = expandidos.has(evento.id);
                            return <Fragment key={evento.id}>
                                <tr>
                                    <td>
                                        {evento.historico.length > 0 &&
                                            <button type="button" className="btn btn-sm btn-icon btn-light" onClick={() => alternar(evento.id)}
                                                aria-label={t('Ver tentativas')}>
                                                <i className={`fas ${aberto ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                                            </button>}
                                    </td>
                                    <td>{formatarDataHora(evento.criadoEm)}</td>
                                    <td><code>{evento.evento}</code></td>
                                    <td>{evento.operacaoId ?? '—'}</td>
                                    <td><span className={`badge ${rotulo.classe}`}>{t(rotulo.texto)}</span></td>
                                    <td>{evento.tentativas}</td>
                                    <td>{ultima ? (ultima.statusHttp != null ? `HTTP ${ultima.statusHttp}` : t(ultima.erro ?? '—')) : '—'}</td>
                                    <td className="text-end">
                                        {podeReenviar(evento.situacao) &&
                                            <button type="button" className="btn btn-sm btn-light-primary" disabled={reenviando === evento.id || !webhook?.ativo}
                                                title={!webhook?.ativo ? t('Ative o webhook para reenviar') : undefined}
                                                onClick={() => reenviar(evento.id)}>
                                                {reenviando === evento.id ? t('Enviando...') : t('Reenviar')}
                                            </button>}
                                    </td>
                                </tr>
                                {aberto && evento.historico.map((tentativa) => (
                                    <tr key={`${evento.id}-${tentativa.tentativa}`} className="bg-light">
                                        <td></td>
                                        <td>{formatarDataHora(tentativa.enviadoEm)}</td>
                                        <td colSpan={2} className="text-break">#{tentativa.tentativa} — {tentativa.url}</td>
                                        <td>
                                            <span className={`badge ${tentativa.entregue ? 'badge-light-success' : 'badge-light-danger'}`}>
                                                {tentativa.entregue ? t('Entregue') : t('Falhou')}
                                            </span>
                                        </td>
                                        <td>{tentativa.duracaoMs != null ? `${tentativa.duracaoMs} ms` : '—'}</td>
                                        <td className="text-break">
                                            {tentativa.statusHttp != null && <>HTTP {tentativa.statusHttp} </>}
                                            {tentativa.erro && <div className="text-danger">{t(tentativa.erro)}</div>}
                                            {tentativa.resposta && <small className="text-muted">{tentativa.resposta.slice(0, 200)}</small>}
                                        </td>
                                        <td></td>
                                    </tr>
                                ))}
                            </Fragment>;
                        })}
                    </tbody>
                </table>
            </div>}
    </>;
}
